// M8-ASSESS: the fake twin of the assessment routes (contracts `api/assess.ts`). Rounds of the
// team X-Ray per company (draft → open → closed → reported), respondents (members and `guest`
// respondents reached by a magic link that expires), their answers, report revisions and the
// org-chart proposals a report makes, with the server's rules: rounds are managers' (403 for a
// member, 404 for another company's ids), 409 `assessment_state_conflict` for a change the round's
// state does not allow, 410 `assessment_link_expired` for a link past its expiry or after the
// round closed, 422 with field errors on an incomplete submission. Scoring is the real engine
// (`@companyos/domain-assess`: buildRoundReport, proposeChart), so the fake's report is the one
// the API computes from the same answers. Applying accepted proposals goes through the twin's own
// org handlers (roles, seats, responsibilities, metrics, seat metrics), so Setup › Seats and the
// chart show what was created.
//
// Seeds (Acme): round 1, a reported full X-Ray with six respondents (three members, three
// guests; the sample answers of a small recruiting firm), and round 2, an open quick scan in
// progress (one answered, one half-way, two pending: one of them the owner's own questionnaire).
import {
  type Answers,
  answeredShare,
  buildRoundReport,
  type ChartSeat,
  EMPTY_ANSWERS,
  isReminderEligible,
  type ProposalDraft,
  proposeChart,
  QUESTION_CHOICES,
  QUESTIONNAIRES,
  type QuestionnaireKey,
  questionnaireSteps,
  type RoundReport,
  SAMPLE_ANSWERS,
  SCORING_VERSION,
  type TranslatorRespondent,
  validateSubmission,
  visibilitySummary,
} from "@companyos/domain-assess";
import type { OperationOutput, Problem, RouteId } from "@companyos/sdk";
import type { Ctx, Principal } from "./fake";
import type { FakeCompany } from "./fake-data";
import { uid } from "./fake-data";
import type { ComposedHandler } from "./fake-merge";
import type { Responsibility, Seat } from "./types";

type Round = OperationOutput<"assessments.getRound">;
type RoundRow = Omit<
  Round,
  "progress" | "respondents" | "report_version" | "participants" | "questionnaire_info" | "links"
>;
type ParticipantRow = Omit<
  Round["participants"][number],
  "answered_share" | "seat_title" | "can_remind"
>;
type ProposalRow = Omit<OperationOutput<"assessments.decideProposal">, "blocked" | "seat_title">;
type DiffLine = OperationOutput<"assessments.previewApply">["lines"][number];
type Link = Round["links"][number];

type Report = {
  id: string;
  round_id: string;
  version: number;
  scoring_version: string;
  body: RoundReport;
  created_at: string;
};

type State = {
  rounds: RoundRow[];
  participants: ParticipantRow[];
  /** participant id → token (the current one; a reminder replaces it). */
  tokens: Map<string, string>;
  answers: Map<string, { answers: Answers; submitted_at: string | null }>;
  reports: Report[];
  proposals: ProposalRow[];
};

export const FAKE_ASSESS_ROUTES = [
  "assessments.listRounds",
  "assessments.createRound",
  "assessments.getRound",
  "assessments.updateRound",
  "assessments.deleteRound",
  "assessments.addParticipants",
  "assessments.removeParticipant",
  "assessments.openRound",
  "assessments.remind",
  "assessments.closeRound",
  "assessments.getReport",
  "assessments.reportHtml",
  "assessments.decideProposal",
  "assessments.previewApply",
  "assessments.apply",
  "assessments.mine",
  "assessments.myLink",
  "respond.get",
  "respond.save",
  "respond.submit",
] as const satisfies readonly RouteId[];

export type AssessDeps = {
  json: (body: unknown, status?: number) => Response;
  problem: (status: number, code: Problem["code"], title: string, detail?: string) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  now: () => Date;
  /** The twin's companies by id (the respondent routes have no principal). */
  companyOf: (id: string) => FakeCompany | null;
  seatsOf: (c: FakeCompany) => Seat[];
  /** Active person holders of seats (membership → seat). */
  personSeatsOf: (c: FakeCompany) => { membership_id: string; seat_id: string }[];
  responsibilitiesOf: (c: FakeCompany) => Responsibility[];
  /** The company's people the round may ask (active person memberships). */
  peopleOf: (c: FakeCompany) => { id: string; display_name: string; owner: boolean }[];
  /** The company's role names → ids (roles.create reuses by name). */
  rolesOf: (companyId: string) => { id: string; name: string }[];
  metricsOf: (c: FakeCompany) => { id: string; key: string; name: string }[];
  /** Calls another twin route (the org writes an application goes through). */
  call: (id: RouteId, ctx: Ctx) => Response | Promise<Response>;
  peek: <T>(res: Response) => T | undefined;
};

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
const MAX_REMINDERS_STATE = new Set(["invited", "opened", "in_progress"]);
/** A respondent's state in X-Ray's invite vocabulary (`isReminderEligible` reads it). */
const INVITE_STATUS: Record<string, string> = {
  invited: "SENT",
  opened: "OPENED",
  in_progress: "IN_PROGRESS",
};

let tokenSeq = 0;
function newToken(seed: string): string {
  tokenSeq += 1;
  const raw = `${seed}${tokenSeq}${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  return `at_${raw
    .replace(/[^a-z0-9]/gi, "")
    .padEnd(40, "x")
    .slice(0, 40)}`;
}

export function createAssessFake(d: AssessDeps): Partial<Record<RouteId, ComposedHandler>> {
  const { json, problem, notFound, validation } = d;
  const states = new Map<string, State>();
  let seq = 0;
  const id = (group: number) => uid(group, ++seq);
  const iso = (t: Date) => t.toISOString();
  const conflict = (detail: string) =>
    problem(409, "assessment_state_conflict", "Not possible in the round's current state", detail);
  const forbidden = () => problem(403, "forbidden", "Forbidden", "managers only");

  const stateOf = (c: FakeCompany): State => {
    let s = states.get(c.id);
    if (!s) {
      s = {
        rounds: [],
        participants: [],
        tokens: new Map(),
        answers: new Map(),
        reports: [],
        proposals: [],
      };
      states.set(c.id, s);
      seed(c, s);
    }
    return s;
  };

  const actorOf = (p: Principal | null) => {
    const m = p?.me.memberships.find((x) => x.company_id === p.company.id);
    return m
      ? { actor_kind: "person" as const, actor_membership_id: m.membership_id }
      : { actor_kind: "system" as const, actor_membership_id: null };
  };
  const myMembership = (p: Principal) =>
    p.me.memberships.find((x) => x.company_id === p.company.id)?.membership_id ?? null;

  const seatOfMember = (c: FakeCompany, membershipId: string) =>
    d.personSeatsOf(c).find((a) => a.membership_id === membershipId)?.seat_id ?? null;

  const isManagerP = (c: FakeCompany, part: ParticipantRow) =>
    part.is_owner ||
    (!!part.seat_id && d.seatsOf(c).some((s) => s.supervisor_seat_id === part.seat_id));

  const answersOf = (s: State, pid: string) => s.answers.get(pid)?.answers ?? EMPTY_ANSWERS;

  const progressOf = (s: State, roundId: string) => {
    const ps = s.participants.filter((x) => x.round_id === roundId);
    const n = (st: string) => ps.filter((x) => x.state === st).length;
    return {
      invited: n("invited"),
      opened: n("opened"),
      in_progress: n("in_progress"),
      completed: n("completed"),
      expired: n("expired"),
    };
  };

  const canRemind = (round: RoundRow, part: ParticipantRow, now: Date) =>
    round.state === "open" &&
    MAX_REMINDERS_STATE.has(part.state) &&
    isReminderEligible(
      {
        status: INVITE_STATUS[part.state] ?? "X",
        expires_at: new Date(part.expires_at ?? 0),
        reminder_count: part.reminder_count,
        last_reminder_at: part.last_reminded_at ? new Date(part.last_reminded_at) : null,
        sent_at: round.opened_at ? new Date(round.opened_at) : null,
      } as never,
      now,
    );

  const summaryOf = (s: State, r: RoundRow) => {
    const latest = s.reports
      .filter((x) => x.round_id === r.id)
      .sort((a, b) => b.version - a.version)[0];
    return {
      ...r,
      progress: progressOf(s, r.id),
      respondents: s.participants.filter((x) => x.round_id === r.id).length,
      report_version: latest?.version ?? null,
    };
  };

  const detailOf = (c: FakeCompany, s: State, r: RoundRow, links: Link[] = []): Round => {
    const now = d.now();
    const seats = d.seatsOf(c);
    const key = r.questionnaire as QuestionnaireKey;
    return {
      ...summaryOf(s, r),
      participants: s.participants
        .filter((x) => x.round_id === r.id)
        .map((x) => ({
          ...x,
          answered_share:
            x.state === "completed" ? 1 : answeredShare(key, isManagerP(c, x), answersOf(s, x.id)),
          seat_title: seats.find((st) => st.id === x.seat_id)?.title ?? null,
          can_remind: canRemind(r, x, now),
        })),
      questionnaire_info: QUESTIONNAIRES[key],
      links,
    };
  };

  const mint = (c: FakeCompany, s: State, part: ParticipantRow, days: number): Link => {
    const token = newToken(part.id);
    s.tokens.set(part.id, token);
    const expires = iso(new Date(d.now().getTime() + days * DAY));
    part.expires_at = expires;
    return {
      participant_id: part.id,
      display_name: part.display_name,
      path: `/respond/${c.id}/${token}`,
      expires_at: expires,
    };
  };

  // People -----------------------------------------------------------------------------------

  type GuestIn = {
    email: string;
    name: string;
    role_title?: string | null;
    reports_to_seat_id?: string | null;
  };

  const addPeople = (
    c: FakeCompany,
    s: State,
    round: RoundRow,
    p: Principal | null,
    memberIds: readonly string[],
    guests: readonly GuestIn[],
  ): ParticipantRow[] | Response => {
    const people = d.peopleOf(c);
    const seats = d.seatsOf(c);
    const at = iso(d.now());
    const made: ParticipantRow[] = [];
    const existing = s.participants.filter((x) => x.round_id === round.id);
    for (const mid of memberIds) {
      const person = people.find((x) => x.id === mid);
      if (!person) return validation(`member_ids: ${mid} is not an active person of the company`);
      if (
        existing.some((x) => x.membership_id === mid) ||
        made.some((x) => x.membership_id === mid)
      )
        continue;
      made.push({
        company_id: c.id,
        id: id(0x5a10),
        round_id: round.id,
        kind: "member",
        membership_id: mid,
        seat_id: seatOfMember(c, mid),
        reports_to_seat_id: null,
        email: null,
        display_name: person.display_name,
        role_title: null,
        is_owner: person.owner,
        state: "invited",
        expires_at: null,
        reminder_count: 0,
        last_reminded_at: null,
        opened_at: null,
        completed_at: null,
        ...actorOf(p),
        created_at: at,
        updated_at: at,
      });
    }
    for (const g of guests) {
      const email = String(g.email ?? "")
        .trim()
        .toLowerCase();
      if (!/^[^@\s]+@[^@\s]+$/.test(email)) return validation("guests.email: an email address");
      if (!String(g.name ?? "").trim()) return validation("guests.name: required");
      if (g.reports_to_seat_id && !seats.some((x) => x.id === g.reports_to_seat_id))
        return validation("guests.reports_to_seat_id: no such seat");
      if (existing.some((x) => x.email === email) || made.some((x) => x.email === email)) continue;
      made.push({
        company_id: c.id,
        id: id(0x5a10),
        round_id: round.id,
        kind: "guest",
        membership_id: null,
        seat_id: null,
        reports_to_seat_id: g.reports_to_seat_id ?? null,
        email,
        display_name: String(g.name).trim(),
        role_title: g.role_title?.trim() || null,
        is_owner: false,
        state: "invited",
        expires_at: null,
        reminder_count: 0,
        last_reminded_at: null,
        opened_at: null,
        completed_at: null,
        ...actorOf(p),
        created_at: at,
        updated_at: at,
      });
    }
    s.participants.push(...made);
    return made;
  };

  // Report -----------------------------------------------------------------------------------

  const respondentsOf = (c: FakeCompany, s: State, roundId: string): TranslatorRespondent[] => {
    const ps = s.participants.filter((x) => x.round_id === roundId);
    const seats = d.seatsOf(c);
    const owner = ps.find((x) => x.is_owner);
    const holderOf = (seatId: string | null) =>
      seatId ? ps.find((x) => x.seat_id === seatId) : undefined;
    return ps.map((x) => {
      const sup = x.seat_id
        ? (seats.find((st) => st.id === x.seat_id)?.supervisor_seat_id ?? null)
        : x.reports_to_seat_id;
      const boss = holderOf(sup) ?? (x.is_owner ? undefined : owner);
      return {
        participant_id: x.id,
        name: x.display_name,
        role_title: x.role_title,
        is_owner: x.is_owner,
        reports_to_id: boss && boss.id !== x.id ? boss.id : null,
        completed: x.state === "completed",
        answers: x.state === "completed" ? answersOf(s, x.id) : null,
        kind: x.kind,
        seat_id: x.seat_id,
        reports_to_seat_id: x.reports_to_seat_id,
      };
    });
  };

  const headSeat = (c: FakeCompany) =>
    d.seatsOf(c).find((x) => !x.supervisor_seat_id && x.status !== "closed")?.id ?? null;

  const generate = (c: FakeCompany, s: State, round: RoundRow, p: Principal | null) => {
    const respondents = respondentsOf(c, s, round.id);
    const body = buildRoundReport({ respondents });
    const version = s.reports.filter((x) => x.round_id === round.id).length + 1;
    const at = iso(d.now());
    const report: Report = {
      id: id(0x5a30),
      round_id: round.id,
      version,
      scoring_version: SCORING_VERSION,
      body,
      created_at: at,
    };
    s.reports.push(report);
    const resps = d.responsibilitiesOf(c);
    const chart: ChartSeat[] = d
      .seatsOf(c)
      .filter((x) => x.status !== "closed")
      .map((x) => ({
        id: x.id,
        title: x.title,
        supervisor_seat_id: x.supervisor_seat_id,
        responsibilities: resps.filter((r) => r.seat_id === x.id).map((r) => r.name),
      }));
    const drafts: ProposalDraft[] = proposeChart({
      report: body,
      respondents,
      seats: chart,
      head_seat_id: headSeat(c),
    });
    drafts.forEach((dr, i) => {
      s.proposals.push({
        company_id: c.id,
        id: id(0x5a40),
        round_id: round.id,
        report_id: report.id,
        key: dr.key,
        kind: dr.kind,
        title: dr.title,
        detail: dr.detail,
        depends_on: dr.depends_on,
        payload: dr.payload as unknown as Record<string, unknown>,
        state: "proposed",
        applied_ref: null,
        applied_at: null,
        position: i,
        ...actorOf(p),
        created_at: at,
        updated_at: at,
      });
    });
    round.state = "reported";
    round.reported_at = at;
    round.updated_at = at;
    return report;
  };

  const latestReport = (s: State, roundId: string) =>
    s.reports.filter((x) => x.round_id === roundId).sort((a, b) => b.version - a.version)[0];

  const proposalView = (c: FakeCompany, s: State, x: ProposalRow) => {
    const dep = x.depends_on
      ? s.proposals.find((y) => y.report_id === x.report_id && y.key === x.depends_on)
      : undefined;
    const seatId =
      typeof x.payload.seat_id === "string"
        ? x.payload.seat_id
        : x.kind === "seat" && typeof x.payload.reports_to_seat_id === "string"
          ? x.payload.reports_to_seat_id
          : null;
    return {
      ...x,
      blocked: !!dep && dep.state !== "accepted" && dep.state !== "applied",
      seat_title: seatId ? (d.seatsOf(c).find((st) => st.id === seatId)?.title ?? null) : null,
    };
  };

  // Apply ------------------------------------------------------------------------------------

  const titleOfSeatProposal = (x: ProposalRow) => String(x.payload.title ?? x.title);

  const plan = (c: FakeCompany, s: State, roundId: string, ids: readonly string[] | undefined) => {
    const latest = latestReport(s, roundId);
    const pool = s.proposals.filter((x) => x.report_id === latest?.id);
    const chosen = ids
      ? pool.filter((x) => ids.includes(x.id))
      : pool.filter((x) => x.state === "accepted");
    const bad = chosen.find((x) => x.state !== "accepted");
    const seats = d.seatsOf(c);
    const roles = d.rolesOf(c.id);
    const metrics = d.metricsOf(c);
    const lines: DiffLine[] = [];
    const order = [...chosen].sort(
      (a, b) =>
        ({ seat: 0, responsibility: 1, metric: 2 })[a.kind] -
          { seat: 0, responsibility: 1, metric: 2 }[b.kind] || a.position - b.position,
    );
    const seatReady = (x: ProposalRow) => {
      if (!x.depends_on) return true;
      const dep = pool.find((y) => y.key === x.depends_on);
      return !!dep && (dep.state === "applied" || chosen.includes(dep));
    };
    const where = (x: ProposalRow): string | null => {
      if (x.depends_on) {
        const dep = pool.find((y) => y.key === x.depends_on);
        return dep ? `${titleOfSeatProposal(dep)} (new)` : null;
      }
      const sid = typeof x.payload.seat_id === "string" ? x.payload.seat_id : null;
      return sid ? (seats.find((st) => st.id === sid)?.title ?? null) : null;
    };
    for (const x of order) {
      if (x.kind === "seat") {
        const roleName = String(x.payload.role_name ?? x.payload.title);
        const role = roles.find((r) => r.name.toLowerCase() === roleName.toLowerCase());
        lines.push({
          proposal_id: x.id,
          kind: "role",
          action: role ? "reuse" : "create",
          name: roleName,
          parent: null,
          reason: role ? "a job role of that name exists" : null,
        });
        const sup =
          typeof x.payload.reports_to_seat_id === "string"
            ? (seats.find((st) => st.id === x.payload.reports_to_seat_id)?.title ?? null)
            : null;
        lines.push({
          proposal_id: x.id,
          kind: "seat",
          action: "create",
          name: titleOfSeatProposal(x),
          parent: sup,
          reason: null,
        });
      } else if (x.kind === "responsibility") {
        const ok = seatReady(x);
        lines.push({
          proposal_id: x.id,
          kind: "responsibility",
          action: ok ? "create" : "skip",
          name: String(x.payload.name),
          parent: where(x),
          reason: ok ? null : "its proposed seat is not accepted",
        });
      } else {
        const ok = seatReady(x);
        const key = String(x.payload.key);
        const def = metrics.find((m) => m.key === key);
        lines.push({
          proposal_id: x.id,
          kind: "metric",
          action: !ok ? "skip" : def ? "reuse" : "create",
          name: String(x.payload.name),
          parent: where(x),
          reason: !ok ? "its proposed seat is not accepted" : def ? `metric ${key} exists` : null,
        });
        lines.push({
          proposal_id: x.id,
          kind: "seat_metric",
          action: ok ? "create" : "skip",
          name: `${String(x.payload.name)} on the seat (weekly target ${String(x.payload.target)})`,
          parent: where(x),
          reason: ok ? null : "its proposed seat is not accepted",
        });
      }
    }
    const counts = { create: 0, reuse: 0, skip: 0 };
    for (const l of lines) counts[l.action] += 1;
    return { lines, counts, order, bad, pool };
  };

  // Handlers ---------------------------------------------------------------------------------

  const guard = (ctx: Ctx): { p: Principal; s: State } | Response => {
    const p = ctx.principal;
    if (!p) return notFound();
    if (!p.manager) return forbidden();
    return { p, s: stateOf(p.company) };
  };
  const roundIn = (s: State, rid: string | undefined) => s.rounds.find((x) => x.id === rid) ?? null;

  const byToken = (co: string, token: string) => {
    const c = d.companyOf(co);
    if (!c) return null;
    const s = stateOf(c);
    for (const [pid, t] of s.tokens)
      if (t === token) {
        const part = s.participants.find((x) => x.id === pid);
        const round = part ? roundIn(s, part.round_id) : null;
        if (part && round) return { c, s, part, round };
      }
    return null;
  };

  const questionnaireView = (c: FakeCompany, s: State, part: ParticipantRow, round: RoundRow) => {
    const key = round.questionnaire as QuestionnaireKey;
    const mgr = isManagerP(c, part);
    const a = answersOf(s, part.id);
    return {
      company_name: c.name,
      round_name: round.name,
      questionnaire: QUESTIONNAIRES[key],
      steps: questionnaireSteps(key, mgr),
      choices: QUESTION_CHOICES,
      respondent: {
        name: part.display_name,
        kind: part.kind,
        role_title: part.role_title,
        is_manager: mgr,
      },
      visibility: visibilitySummary("owner_only", `${c.name}'s owners and admins`),
      answers: { ...a, role_title: a.role_title ?? part.role_title },
      answered_share: part.state === "completed" ? 1 : answeredShare(key, mgr, a),
      state: part.state,
      submitted_at: s.answers.get(part.id)?.submitted_at ?? null,
      expires_at: part.expires_at,
      errors: [],
    };
  };

  const respondGate = (ctx: Ctx, write: boolean) => {
    const hit = byToken(ctx.params.co ?? "", ctx.params.token ?? "");
    if (!hit) return notFound();
    const { round, part } = hit;
    const now = d.now();
    if (
      round.state !== "open" ||
      part.state === "expired" ||
      (part.expires_at && new Date(part.expires_at) <= now)
    ) {
      if (!(part.state === "completed" && !write))
        return problem(410, "assessment_link_expired", "This questionnaire link has expired");
    }
    if (write && part.state === "completed") return conflict("the answers were submitted already");
    return hit;
  };

  const readAnswers = (body: unknown): Answers | Response => {
    const a = (body as { answers?: Partial<Answers> } | null)?.answers;
    if (!a || typeof a !== "object" || !Array.isArray(a.tasks))
      return validation("answers: an object with tasks");
    if (a.tasks.length > 30) return validation("answers.tasks: at most 30");
    return { ...EMPTY_ANSWERS, ...a, tasks: a.tasks } as Answers;
  };

  const handlers: Partial<Record<RouteId, ComposedHandler>> = {
    "assessments.listRounds": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      return json({
        data: [...g.s.rounds].sort((a, b) => b.number - a.number).map((r) => summaryOf(g.s, r)),
        questionnaires: Object.values(QUESTIONNAIRES),
      });
    },
    "assessments.createRound": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const b = (ctx.body ?? {}) as {
        name?: string;
        questionnaire?: string;
        invite_expiry_days?: number;
        member_ids?: string[];
        guests?: GuestIn[];
        open?: boolean;
      };
      if (b.questionnaire !== "full" && b.questionnaire !== "delta")
        return validation("questionnaire: full or delta");
      const days = b.invite_expiry_days ?? 7;
      if (!Number.isInteger(days) || days < 1 || days > 60)
        return validation("invite_expiry_days: 1 to 60");
      const number = g.s.rounds.reduce((m, r) => Math.max(m, r.number), 0) + 1;
      const at = iso(d.now());
      const round: RoundRow = {
        company_id: g.p.company.id,
        id: id(0x5a00),
        number,
        name:
          b.name?.trim() || `${b.questionnaire === "full" ? "Team X-Ray" : "Quick scan"} ${number}`,
        questionnaire: b.questionnaire,
        state: "draft",
        invite_expiry_days: days,
        scoring_version: SCORING_VERSION,
        opened_at: null,
        closed_at: null,
        reported_at: null,
        ...actorOf(g.p),
        created_at: at,
        updated_at: at,
      };
      g.s.rounds.push(round);
      const made = addPeople(g.p.company, g.s, round, g.p, b.member_ids ?? [], b.guests ?? []);
      if (made instanceof Response) {
        g.s.rounds.splice(g.s.rounds.indexOf(round), 1);
        return made;
      }
      let links: Link[] = [];
      if (b.open) links = openRound(g.p.company, g.s, round);
      return json(detailOf(g.p.company, g.s, round, links), 201);
    },
    "assessments.getRound": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const r = roundIn(g.s, ctx.params.id);
      return r ? json(detailOf(g.p.company, g.s, r)) : notFound();
    },
    "assessments.updateRound": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const r = roundIn(g.s, ctx.params.id);
      if (!r) return notFound();
      const b = (ctx.body ?? {}) as { name?: string; invite_expiry_days?: number };
      if (b.name !== undefined && !b.name.trim()) return validation("name: required");
      if (
        b.invite_expiry_days !== undefined &&
        (!Number.isInteger(b.invite_expiry_days) ||
          b.invite_expiry_days < 1 ||
          b.invite_expiry_days > 60)
      )
        return validation("invite_expiry_days: 1 to 60");
      if (r.state !== "draft" && r.state !== "open") return conflict("the round is closed");
      if (b.name !== undefined) r.name = b.name.trim();
      if (b.invite_expiry_days !== undefined) r.invite_expiry_days = b.invite_expiry_days;
      r.updated_at = iso(d.now());
      return json(detailOf(g.p.company, g.s, r));
    },
    "assessments.deleteRound": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const r = roundIn(g.s, ctx.params.id);
      if (!r) return notFound();
      if (r.state !== "draft") return conflict("only a draft round can be deleted");
      g.s.rounds.splice(g.s.rounds.indexOf(r), 1);
      g.s.participants = g.s.participants.filter((x) => x.round_id !== r.id);
      return new Response(null, { status: 204 });
    },
    "assessments.addParticipants": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const r = roundIn(g.s, ctx.params.id);
      if (!r) return notFound();
      if (r.state !== "draft" && r.state !== "open") return conflict("the round is closed");
      const b = (ctx.body ?? {}) as { member_ids?: string[]; guests?: GuestIn[] };
      const made = addPeople(g.p.company, g.s, r, g.p, b.member_ids ?? [], b.guests ?? []);
      if (made instanceof Response) return made;
      const links =
        r.state === "open" ? made.map((x) => mint(g.p.company, g.s, x, r.invite_expiry_days)) : [];
      return json(detailOf(g.p.company, g.s, r, links));
    },
    "assessments.removeParticipant": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const r = roundIn(g.s, ctx.params.id);
      const part = r
        ? g.s.participants.find((x) => x.id === ctx.params.participant_id && x.round_id === r.id)
        : undefined;
      if (!r || !part) return notFound();
      if (part.state === "completed" || (r.state !== "draft" && r.state !== "open"))
        return conflict("they answered already, or the round is closed");
      g.s.participants.splice(g.s.participants.indexOf(part), 1);
      g.s.tokens.delete(part.id);
      g.s.answers.delete(part.id);
      return new Response(null, { status: 204 });
    },
    "assessments.openRound": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const r = roundIn(g.s, ctx.params.id);
      if (!r) return notFound();
      if (r.state !== "draft") return conflict("only a draft round opens");
      if (!g.s.participants.some((x) => x.round_id === r.id)) return conflict("ask someone first");
      return json(detailOf(g.p.company, g.s, r, openRound(g.p.company, g.s, r)));
    },
    "assessments.remind": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const r = roundIn(g.s, ctx.params.id);
      const part = r
        ? g.s.participants.find((x) => x.id === ctx.params.participant_id && x.round_id === r.id)
        : undefined;
      if (!r || !part) return notFound();
      if (!canRemind(r, part, d.now()))
        return conflict(
          "a reminder goes to someone who has not answered, twice at most, 20 h apart",
        );
      part.reminder_count += 1;
      part.last_reminded_at = iso(d.now());
      const link = mint(g.p.company, g.s, part, r.invite_expiry_days);
      const view = detailOf(g.p.company, g.s, r).participants.find((x) => x.id === part.id);
      return json({ participant: view, link });
    },
    "assessments.closeRound": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const r = roundIn(g.s, ctx.params.id);
      if (!r) return notFound();
      if (r.state !== "open") return conflict("only an open round closes");
      closeRound(g.p.company, g.s, r, g.p);
      return json(detailOf(g.p.company, g.s, r));
    },
    "assessments.getReport": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const r = roundIn(g.s, ctx.params.id);
      const rep = r ? latestReport(g.s, r.id) : undefined;
      if (!r || !rep) return notFound();
      return json({
        round: r,
        report_id: rep.id,
        version: rep.version,
        scoring_version: rep.scoring_version,
        generated_at: rep.created_at,
        body: rep.body,
        proposals: g.s.proposals
          .filter((x) => x.report_id === rep.id)
          .sort((a, b) => a.position - b.position)
          .map((x) => proposalView(g.p.company, g.s, x)),
      });
    },
    "assessments.reportHtml": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const r = roundIn(g.s, ctx.params.id);
      const rep = r ? latestReport(g.s, r.id) : undefined;
      if (!r || !rep) return notFound();
      const esc = (t: string) =>
        t.replace(
          /[&<>"]/g,
          (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch] ?? ch,
        );
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(r.name)}</title></head><body><h1>${esc(g.p.company.name)}: ${esc(r.name)}</h1><p>Report v${rep.version} · ${rep.body.participation.completed} of ${rep.body.participation.invited} answered</p><h2>Gaps</h2><ul>${rep.body.gaps.map((x) => `<li>${esc(x.title)}</li>`).join("")}</ul><h2>Recommended actions</h2><ul>${rep.body.actions.map((x) => `<li>${esc(x.title)}</li>`).join("")}</ul></body></html>`;
      return new Response(html, {
        status: 200,
        headers: { "content-type": "text/html; charset=utf-8" },
      });
    },
    "assessments.decideProposal": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const x = g.s.proposals.find((y) => y.id === ctx.params.id);
      if (!x) return notFound();
      const st = (ctx.body as { state?: string } | null)?.state;
      if (st !== "proposed" && st !== "accepted" && st !== "rejected")
        return validation("state: proposed, accepted or rejected");
      if (x.state === "applied") return conflict("the proposal was applied already");
      const latest = latestReport(g.s, x.round_id);
      if (latest?.id !== x.report_id) return conflict("a newer report replaced this proposal");
      x.state = st;
      x.updated_at = iso(d.now());
      return json(proposalView(g.p.company, g.s, x));
    },
    "assessments.previewApply": (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const r = roundIn(g.s, ctx.params.id);
      if (!r || !latestReport(g.s, r.id)) return notFound();
      const ids = (ctx.body as { proposal_ids?: string[] } | null)?.proposal_ids;
      const pl = plan(g.p.company, g.s, r.id, ids);
      if (pl.bad) return validation(`proposal_ids: ${pl.bad.id} is not accepted`);
      return json({ lines: pl.lines, counts: pl.counts });
    },
    "assessments.apply": async (ctx) => {
      const g = guard(ctx);
      if (g instanceof Response) return g;
      const r = roundIn(g.s, ctx.params.id);
      if (!r || !latestReport(g.s, r.id)) return notFound();
      const ids = (ctx.body as { proposal_ids?: string[] } | null)?.proposal_ids;
      const pl = plan(g.p.company, g.s, r.id, ids);
      if (pl.bad) return conflict(`${pl.bad.title} is not accepted`);
      const created = {
        roles: [] as string[],
        seats: [] as string[],
        responsibilities: [] as string[],
        metrics: [] as string[],
      };
      const applied: ProposalRow[] = [];
      const inner = (
        route: RouteId,
        params: Record<string, string>,
        body: unknown,
        headers?: Record<string, string>,
      ) =>
        d.call(route, {
          url: new URL(`http://fake.local/assess-apply`),
          params: { co: g.p.company.id, ...params },
          body,
          me: g.p.me,
          principal: g.p,
          headers: new Headers(headers),
        });
      const out = async <T>(res: Response | Promise<Response>) => {
        const x = await res;
        return x.ok ? (d.peek<T>(x) ?? ((await x.clone().json()) as T)) : null;
      };
      const seatFor = (x: ProposalRow): string | null => {
        if (typeof x.payload.seat_id === "string") return x.payload.seat_id;
        const dep = pl.pool.find((y) => y.key === x.depends_on);
        return dep?.applied_ref?.seat_id ?? null;
      };
      for (const x of pl.order) {
        const at = iso(d.now());
        if (x.kind === "seat") {
          const roleName = String(x.payload.role_name ?? x.payload.title);
          let roleId = d
            .rolesOf(g.p.company.id)
            .find((ro) => ro.name.toLowerCase() === roleName.toLowerCase())?.id;
          if (!roleId) {
            roleId = (await out<{ id: string }>(inner("roles.create", {}, { name: roleName })))?.id;
            if (roleId) created.roles.push(roleId);
          }
          if (!roleId) continue;
          const seat = await out<Seat>(
            inner(
              "seats.create",
              {},
              {
                role_id: roleId,
                title: titleOfSeatProposal(x),
                supervisor_seat_id: (x.payload.reports_to_seat_id as string | null) ?? null,
              },
            ),
          );
          if (!seat) continue;
          const stored = d.seatsOf(g.p.company).find((st) => st.id === seat.id);
          if (stored)
            Object.assign(stored, {
              origin: "assessment",
              origin_ref: { system: "assessment", id: x.id },
            });
          created.seats.push(seat.id);
          x.applied_ref = { seat_id: seat.id, role_id: roleId };
        } else if (x.kind === "responsibility") {
          const seatId = seatFor(x);
          if (!seatId) continue;
          const resp = await out<{ id: string }>(
            inner("responsibilities.create", { id: seatId }, { name: String(x.payload.name) }),
          );
          if (!resp) continue;
          created.responsibilities.push(resp.id);
          x.applied_ref = { responsibility_id: resp.id };
        } else {
          const seatId = seatFor(x);
          if (!seatId) continue;
          const key = String(x.payload.key);
          let defId = d.metricsOf(g.p.company).find((m) => m.key === key)?.id;
          if (!defId) {
            defId = (
              await out<{ id: string }>(
                inner(
                  "metrics.create",
                  {},
                  {
                    key,
                    name: String(x.payload.name),
                    unit: "number",
                    direction: "gte",
                    aggregation: "cumulative",
                    cadence: "weekly",
                    source: "manual",
                  },
                ),
              )
            )?.id;
            if (defId) created.metrics.push(defId);
          }
          if (!defId) continue;
          const set = await out<{ version: number }>(
            inner("seats.metrics", { id: seatId }, undefined),
          );
          await inner(
            "seats.putMetric",
            { id: seatId, metric_id: defId },
            { kpi_weight: "1", goal_weight: "0", tracking_mode: "scored", penalty_only: false },
            { "if-match": `"${set?.version ?? 0}"` },
          );
          x.applied_ref = { metric_def_id: defId };
        }
        x.state = "applied";
        x.applied_at = at;
        x.updated_at = at;
        applied.push(x);
      }
      return json({
        applied: applied.map((x) => proposalView(g.p.company, g.s, x)),
        created,
        lines: pl.lines,
      });
    },
    "assessments.mine": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const s = stateOf(p.company);
      const mine = myMembership(p);
      return json({
        data: s.participants
          .filter(
            (x) => x.membership_id === mine && x.state !== "completed" && x.state !== "expired",
          )
          .flatMap((x) => {
            const r = roundIn(s, x.round_id);
            return r && r.state === "open"
              ? [
                  {
                    round_id: r.id,
                    round_name: r.name,
                    questionnaire: r.questionnaire,
                    participant_id: x.id,
                    state: x.state,
                    expires_at: x.expires_at,
                    minutes: QUESTIONNAIRES[r.questionnaire as QuestionnaireKey].minutes,
                  },
                ]
              : [];
          }),
      });
    },
    "assessments.myLink": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const s = stateOf(p.company);
      const r = roundIn(s, ctx.params.id);
      const mine = myMembership(p);
      const part = r
        ? s.participants.find((x) => x.round_id === r.id && x.membership_id === mine)
        : undefined;
      if (!r || !part) return notFound();
      if (r.state !== "open" || part.state === "completed" || part.state === "expired")
        return conflict("the round is not open for you");
      const link = mint(p.company, s, part, r.invite_expiry_days);
      return json({ path: link.path, expires_at: link.expires_at });
    },
    "respond.get": (ctx) => {
      const hit = respondGate(ctx, false);
      if (hit instanceof Response) return hit;
      if (hit.part.state === "invited") {
        hit.part.state = "opened";
        hit.part.opened_at = iso(d.now());
      }
      return json(questionnaireView(hit.c, hit.s, hit.part, hit.round));
    },
    "respond.save": (ctx) => {
      const hit = respondGate(ctx, true);
      if (hit instanceof Response) return hit;
      const a = readAnswers(ctx.body);
      if (a instanceof Response) return a;
      hit.s.answers.set(hit.part.id, { answers: a, submitted_at: null });
      hit.part.state = "in_progress";
      hit.part.updated_at = iso(d.now());
      return json(questionnaireView(hit.c, hit.s, hit.part, hit.round));
    },
    "respond.submit": (ctx) => {
      const hit = respondGate(ctx, true);
      if (hit instanceof Response) return hit;
      const a = readAnswers(ctx.body);
      if (a instanceof Response) return a;
      hit.s.answers.set(hit.part.id, { answers: a, submitted_at: null });
      const errors = validateSubmission(
        hit.round.questionnaire as QuestionnaireKey,
        isManagerP(hit.c, hit.part),
        a,
      );
      if (errors.length > 0) {
        hit.part.state = "in_progress";
        return new Response(
          JSON.stringify({
            type: "https://companyos.app/errors/validation_failed",
            title: "Validation failed",
            status: 422,
            code: "validation_failed",
            detail: errors.map((e) => e.message).join("; "),
            errors: errors.map((e) => ({ path: e.path, code: "invalid", message: e.message })),
            request_id: "fake-assess",
          }),
          { status: 422, headers: { "content-type": "application/problem+json" } },
        );
      }
      const at = iso(d.now());
      hit.s.answers.set(hit.part.id, { answers: a, submitted_at: at });
      hit.part.state = "completed";
      hit.part.completed_at = at;
      hit.part.updated_at = at;
      return json(questionnaireView(hit.c, hit.s, hit.part, hit.round));
    },
  };

  function openRound(c: FakeCompany, s: State, r: RoundRow): Link[] {
    const at = iso(d.now());
    r.state = "open";
    r.opened_at = at;
    r.updated_at = at;
    return s.participants
      .filter((x) => x.round_id === r.id)
      .map((x) => mint(c, s, x, r.invite_expiry_days));
  }

  function closeRound(c: FakeCompany, s: State, r: RoundRow, p: Principal | null) {
    const at = iso(d.now());
    r.state = "closed";
    r.closed_at = at;
    for (const x of s.participants.filter((y) => y.round_id === r.id && y.state !== "completed")) {
      x.state = "expired";
      x.updated_at = at;
      s.tokens.delete(x.id);
    }
    generate(c, s, r, p);
  }

  // Seeds ------------------------------------------------------------------------------------

  function seed(c: FakeCompany, s: State) {
    if (c.slug !== "acme") return;
    const people = d.peopleOf(c);
    const owner = people.find((x) => x.owner);
    // Seat holders first: they carry the "head" persona (a manager with reports).
    const others = people
      .filter((x) => !x.owner)
      .sort((a, b) => Number(!!seatOfMember(c, b.id)) - Number(!!seatOfMember(c, a.id)));
    const head = headSeat(c);
    const now = d.now().getTime();
    const mk = (
      number: number,
      name: string,
      questionnaire: QuestionnaireKey,
      opened: number,
    ): RoundRow => ({
      company_id: c.id,
      id: id(0x5a00),
      number,
      name,
      questionnaire,
      state: "draft",
      invite_expiry_days: 7,
      scoring_version: SCORING_VERSION,
      opened_at: iso(new Date(now - opened * DAY)),
      closed_at: null,
      reported_at: null,
      actor_kind: "system",
      actor_membership_id: null,
      created_at: iso(new Date(now - opened * DAY - HOUR)),
      updated_at: iso(new Date(now - opened * DAY)),
    });
    const guests = (list: [string, string, string][]) =>
      list.map(([name, email, role]) => ({
        name,
        email,
        role_title: role,
        reports_to_seat_id: head,
      }));

    // Round 1: reported full X-Ray, six respondents.
    const r1 = mk(1, "Team X-Ray — Q3", "full", 30);
    s.rounds.push(r1);
    const members = [owner?.id, ...others.map((x) => x.id)]
      .filter((x): x is string => !!x)
      .slice(0, 3);
    const made1 = addPeople(
      c,
      s,
      r1,
      null,
      members,
      guests([
        ["Priya Shah", "priya.shah@acme-guest.example", "Account manager"],
        ["Elena Rossi", "elena.rossi@acme-guest.example", "Sourcer"],
        ["Alex Kim", "alex.kim@acme-guest.example", "Part-time bookkeeper"],
      ]),
    );
    if (made1 instanceof Response) return;
    const personas1 = ["owner", "head", "senior", "am", "sourcer", "bookkeeper"] as const;
    const byOrder = [
      ...made1.filter((x) => x.is_owner),
      ...made1.filter((x) => !x.is_owner && x.kind === "member"),
      ...made1.filter((x) => x.kind === "guest"),
    ];
    byOrder.forEach((x, i) => {
      const persona = personas1[i] ?? "senior";
      const at = iso(new Date(now - (28 - i) * DAY));
      x.state = "completed";
      x.completed_at = at;
      x.opened_at = at;
      x.expires_at = iso(new Date(now - 23 * DAY));
      x.role_title = x.role_title ?? SAMPLE_ANSWERS[persona].role_title;
      s.answers.set(x.id, { answers: SAMPLE_ANSWERS[persona], submitted_at: at });
    });
    r1.state = "closed";
    r1.closed_at = iso(new Date(now - 21 * DAY));
    generate(c, s, r1, null);
    r1.reported_at = r1.closed_at;
    const rep = latestReport(s, r1.id);
    if (rep) rep.created_at = r1.closed_at;

    // Round 2: an open quick scan in progress.
    const r2 = mk(2, "Quick scan — October", "delta", 2);
    s.rounds.push(r2);
    const made2 = addPeople(
      c,
      s,
      r2,
      null,
      members,
      guests([["Priya Shah", "priya.shah@acme-guest.example", "Account manager"]]),
    );
    if (made2 instanceof Response) return;
    r2.state = "open";
    for (const x of made2) {
      const token = newToken(x.id);
      s.tokens.set(x.id, token);
      x.expires_at = iso(new Date(now + 5 * DAY));
    }
    const [first, second] = made2.filter((x) => !x.is_owner);
    if (first) {
      const at = iso(new Date(now - DAY));
      first.state = "completed";
      first.opened_at = at;
      first.completed_at = at;
      s.answers.set(first.id, {
        answers: { ...SAMPLE_ANSWERS.head, role_title: null, clarity: null },
        submitted_at: at,
      });
    }
    if (second) {
      second.state = "in_progress";
      second.opened_at = iso(new Date(now - DAY));
      s.answers.set(second.id, {
        answers: { ...EMPTY_ANSWERS, tasks: SAMPLE_ANSWERS.senior.tasks.slice(0, 2) },
        submitted_at: null,
      });
    }
    const guest = made2.find((x) => x.kind === "guest");
    if (guest) {
      guest.state = "opened";
      guest.opened_at = iso(new Date(now - 6 * HOUR));
    }
  }

  return handlers;
}
